import React, { useEffect, useState } from 'react';
import { useSector } from '../../contexts/SectorContext';
import { summarizeConversation, suggestReply, createApproval } from '../services/MendsVisionService';
import { describeCreateOutcome } from '../lib/createApprovalOutcome';
import '../styles/mendsAi.css';

interface Props {
  contactId: number;
  contactName?: string;
  onUseSuggestion?: (text: string) => void;
  onSendSuggestion?: (text: string) => void;
  onAssume?: () => void;
}

export const InboxAiAssist: React.FC<Props> = ({
  contactId,
  contactName,
  onUseSuggestion,
  onSendSuggestion,
  onAssume,
}) => {
  const { selectedSectorId } = useSector();
  const [summary, setSummary] = useState('');
  const [suggest, setSuggest] = useState('');
  const [loading, setLoading] = useState<'summary' | 'suggest' | null>(null);
  const [open, setOpen] = useState(false);
  const [queued, setQueued] = useState(false);
  const [queueNote, setQueueNote] = useState<string | null>(null);

  useEffect(() => {
    setSummary('');
    setSuggest('');
    setOpen(false);
    setQueued(false);
    setQueueNote(null);
  }, [contactId]);

  const run = async (kind: 'summary' | 'suggest') => {
    if (!selectedSectorId) return;
    setLoading(kind);
    setOpen(true);
    if (kind === 'suggest') {
      // Nova sugestão = novo texto: a nota e o "Na fila" eram da sugestão anterior.
      setQueueNote(null);
      setQueued(false);
    }
    try {
      const res = kind === 'summary'
        ? await summarizeConversation(Number(selectedSectorId), contactId)
        : await suggestReply(Number(selectedSectorId), contactId);
      if (kind === 'summary') setSummary(res.text);
      else setSuggest(res.text);
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'Não foi possível gerar agora.';
      if (kind === 'summary') setSummary(msg);
      else setSuggest(msg);
    } finally {
      setLoading(null);
    }
  };

  const queueSend = async () => {
    if (!selectedSectorId || !suggest) return;
    const created = await createApproval({
      sectorId: Number(selectedSectorId),
      contactId,
      contactName,
      title: `Enviar mensagem — ${contactName || 'contato'}`,
      body: suggest,
      suggestedAction: 'Enviar no WhatsApp',
      actionKind: 'send_message',
      actionPayload: JSON.stringify({ text: suggest, contactId }),
    });
    const outcome = describeCreateOutcome(created);
    if (outcome.kind !== 'queued') {
      setQueueNote(outcome.message);
      return;
    }
    setQueueNote(null);
    setQueued(true);
  };

  return (
    <>
      <div className="mai-assist-bar">
        <button className="mai-btn mai-btn-soft" onClick={() => void run('summary')} disabled={!!loading}>
          Resumo da IA
        </button>
        <button className="mai-btn mai-btn-soft" onClick={() => void run('suggest')} disabled={!!loading}>
          Sugestão pronta
        </button>
        {onAssume && (
          <button className="mai-btn mai-btn-ghost" onClick={onAssume}>Assumir conversa</button>
        )}
      </div>
      {open && (
        <div className="mai-assist-panel">
          {(summary || loading === 'summary') && (
            <>
              <div className="mai-section-label">Resumo da IA</div>
              <p className="mai-card-detail">{loading === 'summary' ? 'Gerando…' : summary}</p>
            </>
          )}
          {(suggest || loading === 'suggest') && (
            <>
              <div className="mai-section-label" style={{ marginTop: 10 }}>Sugestão pronta da IA</div>
              <p className="mai-card-detail">{loading === 'suggest' ? 'Gerando…' : suggest}</p>
              {!loading && suggest && (
                <div className="mai-btn-row">
                  {onSendSuggestion && (
                    <button className="mai-btn" onClick={() => onSendSuggestion(suggest)}>Enviar</button>
                  )}
                  {onUseSuggestion && (
                    <button className="mai-btn mai-btn-soft" onClick={() => onUseSuggestion(suggest)}>Editar</button>
                  )}
                  <button className="mai-btn mai-btn-ghost" onClick={() => void queueSend()} disabled={queued}>
                    {queued ? 'Na fila' : 'Fila de aprovação'}
                  </button>
                </div>
              )}
              {queueNote && <p className="mai-form-hint" role="status">{queueNote}</p>}
            </>
          )}
        </div>
      )}
    </>
  );
};
